/**
 * Veda Lead Triage menu rule (Manak 2026-10-07).
 *
 * For a Veda tenant (module `veda_commodity_deals` explicitly enabled) and the
 * roles the backend lets in (admin, owner, super_admin, md):
 *   - the "Leads" item (/leads) opens Lead Triage (/veda-agro/triage) instead;
 *   - if the menu has no Leads item (e.g. a scope-built menu), Lead Triage is
 *     added at the top of the first section;
 *   - the separate "Routing" item (/veda-agro/triage) is removed.
 * Everyone else on a Veda tenant loses any /veda-agro/triage item.
 * Non-Veda tenants (Sun Stellar): menu returned untouched — same object.
 *
 * Must match the backend guard requireRole('ADMIN','OWNER','MD') in
 * backend/src/modules/veda-agro/lead-inbox.routes.ts.
 */
export const LEAD_TRIAGE_PATH = '/veda-agro/triage';
export const LEAD_TRIAGE_SUMMARY_URL = '/veda-agro/lead-inbox/triage/summary';
export const LEAD_TRIAGE_ROLES: readonly string[] = ['admin', 'owner', 'super_admin', 'md'];
const VEDA_MODULE = 'veda_commodity_deals';
const LEGACY_LEADS_PATH = '/leads';

interface NavItemLike { to: string; label: string; module?: string; roleNamesOnly?: string[] }
interface SectionLike<I extends NavItemLike> { label: string; items: I[]; seeMore?: I[] }

export function isVedaTenant(enabledModules: Set<string> | null): boolean {
  return !!enabledModules && enabledModules.has(VEDA_MODULE);
}

export function canSeeLeadTriage(roleName: string, enabledModules: Set<string> | null): boolean {
  return isVedaTenant(enabledModules) && LEAD_TRIAGE_ROLES.includes((roleName ?? '').toLowerCase());
}

export function applyVedaLeadsNav<I extends NavItemLike, S extends SectionLike<I>>(
  sections: S[],
  ctx: { roleName: string; enabledModules: Set<string> | null; triageItem: Omit<I, 'to' | 'label' | 'module' | 'roleNamesOnly'> },
): S[] {
  if (!isVedaTenant(ctx.enabledModules)) return sections;
  const allowed = canSeeLeadTriage(ctx.roleName, ctx.enabledModules);
  const triage = {
    ...ctx.triageItem,
    to: LEAD_TRIAGE_PATH,
    label: 'Leads',
    module: VEDA_MODULE,
    roleNamesOnly: [...LEAD_TRIAGE_ROLES],
  } as unknown as I;

  let placed = false;
  const swap = (items: I[]): I[] =>
    items.flatMap((item) => {
      if (item.to === LEAD_TRIAGE_PATH) return [];
      if (item.to === LEGACY_LEADS_PATH && allowed) {
        if (placed) return [];
        placed = true;
        return [triage];
      }
      return [item];
    });

  const out = sections.map((s) => ({ ...s, items: swap(s.items), seeMore: s.seeMore ? swap(s.seeMore) : s.seeMore }));
  if (allowed && !placed && out.length > 0) out[0] = { ...out[0], items: [triage, ...out[0].items] };
  return out;
}
